/**
 * Developer Portal — SUPERADMIN-only.
 *
 * Three sub-tabs:
 *   - Tenants                 → approved partners with active API keys
 *   - API approval requests   → PENDING applications waiting for review
 *   - Log details about API   → developer_audit timeline + per-developer drill-down
 *
 * Approve generates a new API key and shows it to the SuperAdmin ONCE
 * (also emailed to the partner). The plaintext key is never queryable
 * again — only its sha256 hash is persisted, so a leak of the DB can't
 * replay against the API.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  KeyRound, Check, X, ShieldOff, FileText, Mail, Building2,
  ExternalLink, Copy, Send, ArrowDownToLine, RotateCw, MapPin, Trash2, Plus,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_COLOURS = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
  REVOKED: "bg-gray-200 text-gray-700 border-gray-300",
};

const PUBLIC_FORM_URL = `${window.location.origin}/developer/apply`;

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function StatusBadge({ status }) {
  return (
    <Badge className={`${STATUS_COLOURS[status] || "bg-gray-100"} text-xs`}>
      {status}
    </Badge>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Approve dialog — shows plaintext key once, then locks it behind sha256.
// ───────────────────────────────────────────────────────────────────────────
function ApproveDialog({ dev, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null); // {api_key, inbound_secret, id}

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/developers/${dev.id}/approve`, { notes });
      setIssued({ api_key: data.api_key, inbound_secret: data.inbound_secret, id: data.id });
      toast.success(`Approved & emailed credentials to ${dev.email}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setIssued(null); setNotes(""); } setOpen(v); }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`dev-approve-${dev.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Check className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{issued ? "Approved · save these credentials" : `Approve ${dev.name}?`}</DialogTitle>
        </DialogHeader>
        {!issued && (
          <>
            <p className="text-sm text-gray-600">
              Generates an outbound <strong>API key</strong> (Bearer token) <em>and</em> an inbound{" "}
              <strong>HMAC secret</strong> for the POS webhook. Both are emailed to{" "}
              <strong>{dev.email}</strong>. This is the only time the plaintext values are visible.
            </p>
            <Textarea
              placeholder="Optional internal notes (e.g. partner agreement reference)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="dev-approve-notes"
              rows={3}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                {busy ? "Generating…" : "Approve & email credentials"}
              </Button>
            </DialogFooter>
          </>
        )}
        {issued && (
          <>
            <p className="text-sm text-gray-600">
              Saved a copy to <strong>{dev.email}</strong>. Closing this dialog will permanently hide both plaintext values.
            </p>

            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">1 · API key (Bearer token)</div>
              <div className="bg-[#0F1A30] text-[#C9A84C] font-mono text-xs p-3 rounded-lg overflow-auto" data-testid="dev-issued-key">
                {issued.api_key}
              </div>
              <Button variant="outline" size="sm" onClick={() => copy(issued.api_key, "API key")}><Copy className="h-3.5 w-3.5 mr-1" /> Copy key</Button>
            </div>

            <div className="space-y-1 pt-2">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">2 · Inbound HMAC secret (POS webhook)</div>
              <div className="bg-[#0F1A30] text-[#C9A84C] font-mono text-xs p-3 rounded-lg overflow-auto" data-testid="dev-issued-inbound-secret">
                {issued.inbound_secret}
              </div>
              <div className="text-[11px] text-gray-500 font-mono">Developer ID: {issued.id || dev.id}</div>
              <Button variant="outline" size="sm" onClick={() => copy(issued.inbound_secret, "Inbound secret")}><Copy className="h-3.5 w-3.5 mr-1" /> Copy secret</Button>
            </div>

            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="bg-[#1A2744]">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Rotate inbound secret — shows new plaintext once.
// ───────────────────────────────────────────────────────────────────────────
function RotateInboundDialog({ dev, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/developers/${dev.id}/rotate-inbound-secret`);
      setNewSecret(data.inbound_secret);
      toast.success("Inbound secret rotated");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rotate failed");
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(newSecret); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setNewSecret(null); setOpen(v); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`dev-rotate-inbound-${dev.id}`}>
          <RotateCw className="h-3.5 w-3.5 mr-1" /> Rotate secret
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{newSecret ? "New inbound secret" : `Rotate inbound secret for ${dev.name}?`}</DialogTitle>
        </DialogHeader>
        {!newSecret && (
          <>
            <p className="text-sm text-gray-600">
              Generates a fresh HMAC secret. Any in-flight POS requests using the previous secret
              will start failing immediately — share the new secret with the partner before rotating.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={rotate} disabled={busy} className="bg-[#1A2744]">
                {busy ? "Rotating…" : "Rotate now"}
              </Button>
            </DialogFooter>
          </>
        )}
        {newSecret && (
          <>
            <p className="text-sm text-gray-600">Save this secret somewhere safe — we won&apos;t show it again.</p>
            <div className="bg-[#0F1A30] text-[#C9A84C] font-mono text-xs p-3 rounded-lg overflow-auto" data-testid="dev-rotated-secret">
              {newSecret}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={copy}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
              <Button onClick={() => setOpen(false)} className="bg-[#1A2744]">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ dev, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 2) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/developers/${dev.id}/reject`, { reason });
      toast.success(`Rejected — emailed reason to ${dev.email}`);
      onDone(); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`dev-reject-${dev.id}`} className="border-red-300 text-red-700 hover:bg-red-50">
          <X className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Reject {dev.name}?</DialogTitle></DialogHeader>
        <Textarea
          placeholder="Reason (will be emailed to the partner)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          data-testid="dev-reject-reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700">
            {busy ? "Sending…" : "Reject & email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeButton({ dev, onDone }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (!window.confirm(`Revoke ${dev.name}'s API key? Their integration will stop working immediately.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/developers/${dev.id}/revoke`);
      toast.success("API key revoked");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={click}
      disabled={busy}
      data-testid={`dev-revoke-${dev.id}`}
      className="border-gray-300 text-gray-700 hover:bg-gray-50"
    >
      <ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke
    </Button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Per-developer audit drawer
// ───────────────────────────────────────────────────────────────────────────
function DeveloperAuditTimeline({ dev }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/developers/${dev.id}/audit`);
        if (!cancelled) setRows(data.audit || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [dev.id]);
  return (
    <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid={`dev-audit-${dev.id}`}>
      {!rows && <div className="text-sm text-gray-400">Loading…</div>}
      {rows && rows.length === 0 && <div className="text-sm text-gray-500">No events yet.</div>}
      {rows && rows.map((r) => (
        <div key={r.id} className="flex items-start gap-3 border-l-2 border-[#C9A84C]/40 pl-3 py-1">
          <div className="text-xs font-mono text-gray-500 w-40 flex-shrink-0">{fmt(r.created_at)}</div>
          <div className="flex-1 text-sm">
            <span className="font-semibold text-[#1A2744]">{r.action}</span>
            {r.actor_name && <span className="text-gray-500"> · by {r.actor_name}</span>}
            {r.detail && Object.keys(r.detail).length > 0 && (
              <div className="text-xs text-gray-500 mt-0.5 font-mono">{JSON.stringify(r.detail)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────────────────────
export default function DeveloperPortal() {
  const [list, setList] = useState([]);
  const [activeTab, setActiveTab] = useState("tenants");
  const [activeDev, setActiveDev] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/developers");
      setList(data.developers || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load developers");
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/developers");
        if (!cancelled) setList(data.developers || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Failed to load developers");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tenants = useMemo(() => list.filter((d) => d.status === "APPROVED"), [list]);
  const pending = useMemo(() => list.filter((d) => d.status === "PENDING"), [list]);
  const archived = useMemo(() => list.filter((d) => d.status === "REJECTED" || d.status === "REVOKED"), [list]);

  return (
    <div className="space-y-6" data-testid="developer-portal">
      <Card>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <KeyRound className="h-6 w-6 text-[#C9A84C]" />
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Public application form</div>
              <a href={PUBLIC_FORM_URL} target="_blank" rel="noreferrer" className="text-sm font-mono text-[#1A2744] hover:underline">
                {PUBLIC_FORM_URL} <ExternalLink className="inline h-3.5 w-3.5 ml-1" />
              </a>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              asChild
              data-testid="dev-download-guide"
            >
              <a href="/api/developer-guide.pdf" target="_blank" rel="noreferrer">
                <FileText className="h-3.5 w-3.5 mr-1" /> Integration guide (PDF)
              </a>
            </Button>
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(PUBLIC_FORM_URL); toast.success("Link copied"); }}
              data-testid="dev-copy-form-link"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-gray-200 p-1 rounded-lg">
          <TabsTrigger value="tenants" data-testid="dev-tab-tenants" className="data-[state=active]:bg-[#1A2744] data-[state=active]:text-white px-4 py-1.5">
            <Building2 className="h-4 w-4 mr-2" /> Tenants <Badge className="ml-2 bg-emerald-100 text-emerald-800 text-[10px]">{tenants.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="dev-tab-requests" className="data-[state=active]:bg-[#1A2744] data-[state=active]:text-white px-4 py-1.5">
            <Send className="h-4 w-4 mr-2" /> API approval requests <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">{pending.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="dev-tab-logs" className="data-[state=active]:bg-[#1A2744] data-[state=active]:text-white px-4 py-1.5">
            <FileText className="h-4 w-4 mr-2" /> Log details about API
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="pt-4">
          <DeveloperTable
            rows={tenants}
            emptyHint="No approved partners yet. Approve a request to provision one."
            renderActions={(d) => <RevokeButton dev={d} onDone={reload} />}
            onSelect={(d) => { setActiveDev(d); setActiveTab("logs"); }}
          />
          {archived.length > 0 && (
            <div className="mt-6">
              <h3 className="font-heading text-sm uppercase tracking-widest text-gray-500 mb-2">Archived (rejected / revoked)</h3>
              <DeveloperTable rows={archived} emptyHint="" renderActions={() => null} onSelect={(d) => { setActiveDev(d); setActiveTab("logs"); }} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="pt-4">
          <DeveloperTable
            rows={pending}
            emptyHint="No pending requests. Share the public form link to invite developers."
            renderActions={(d) => (
              <div className="flex gap-2 justify-end">
                <ApproveDialog dev={d} onDone={reload} />
                <RejectDialog dev={d} onDone={reload} />
              </div>
            )}
            onSelect={(d) => { setActiveDev(d); setActiveTab("logs"); }}
          />
        </TabsContent>

        <TabsContent value="logs" className="pt-4 space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Pick a developer to inspect</div>
              <div className="flex flex-wrap gap-2" data-testid="dev-log-picker">
                {list.length === 0 && <div className="text-sm text-gray-400">No developers yet.</div>}
                {list.map((d) => (
                  <Button
                    key={d.id}
                    size="sm"
                    variant={activeDev?.id === d.id ? "default" : "outline"}
                    onClick={() => setActiveDev(d)}
                    className={activeDev?.id === d.id ? "bg-[#1A2744]" : ""}
                    data-testid={`dev-log-pick-${d.id}`}
                  >
                    {d.name} · <StatusBadge status={d.status} />
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
          {activeDev && (
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Name</span><div className="font-semibold">{activeDev.name}</div></div>
                  <div><span className="text-gray-500">Email</span><div className="font-semibold">{activeDev.email}</div></div>
                  <div><span className="text-gray-500">Company</span><div>{activeDev.company || "—"}</div></div>
                  <div><span className="text-gray-500">Status</span><div><StatusBadge status={activeDev.status} /></div></div>
                  <div><span className="text-gray-500">Key fingerprint</span><div className="font-mono text-xs">{activeDev.key_last4 ? `••••${activeDev.key_last4}` : "—"}</div></div>
                  <div><span className="text-gray-500">Requests served</span><div className="font-mono">{activeDev.request_count || 0}</div></div>
                  <div><span className="text-gray-500">Last used</span><div className="text-xs">{fmt(activeDev.last_used_at)}</div></div>
                  <div><span className="text-gray-500">Created</span><div className="text-xs">{fmt(activeDev.created_at)}</div></div>
                </div>
                {activeDev.status === "APPROVED" && (
                  <>
                    <InboundWebhookCard dev={activeDev} onRotated={reload} />
                    <TerminalsCard dev={activeDev} />
                  </>
                )}
                <DeveloperAuditTimeline dev={activeDev} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InboundWebhookCard({ dev, onRotated }) {
  const inboundUrl = `${window.location.origin}/api/v1/pos/clock`;
  const copy = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };
  return (
    <div className="rounded-lg border border-[#C9A84C]/30 bg-[#FBF7EC] p-4 space-y-3" data-testid="inbound-webhook-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-[#C9A84C]/20"><ArrowDownToLine className="h-4 w-4 text-[#1A2744]" /></div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Inbound integration</div>
            <h4 className="font-heading text-base font-bold text-[#1A2744]">POS webhook — POS terminal → Portal</h4>
          </div>
        </div>
        <RotateInboundDialog dev={dev} onDone={onRotated} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <div className="space-y-0.5">
          <div className="text-[11px] text-gray-500">Endpoint URL</div>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 flex-1 truncate" data-testid="inbound-webhook-url">{inboundUrl}</code>
            <Button size="sm" variant="outline" onClick={() => copy(inboundUrl, "URL")}><Copy className="h-3 w-3" /></Button>
          </div>
        </div>
        <div className="space-y-0.5">
          <div className="text-[11px] text-gray-500">Developer ID</div>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 flex-1 truncate" data-testid="inbound-dev-id">{dev.id}</code>
            <Button size="sm" variant="outline" onClick={() => copy(dev.id, "Developer ID")}><Copy className="h-3 w-3" /></Button>
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500">HMAC secret fingerprint</div>
          <div className="font-mono text-xs">{dev.inbound_secret_last4 ? `••••${dev.inbound_secret_last4}` : "—"}</div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500">Inbound requests served</div>
          <div className="font-mono text-xs">{dev.inbound_request_count || 0}{dev.inbound_last_used_at ? ` · last ${fmt(dev.inbound_last_used_at)}` : ""}</div>
        </div>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-[#1A2744] font-semibold">Request contract</summary>
        <div className="mt-2 space-y-1 text-gray-700">
          <p>Every POST must include:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li><code>X-CLC-Developer-Id: {dev.id}</code></li>
            <li><code>X-CLC-Signature: sha256=&lt;hex&gt;</code> — HMAC-SHA256 of the raw body using the shared secret</li>
            <li><em>Optional</em>: <code>X-CLC-Event-Id: &lt;uuid&gt;</code> for idempotency (POS retries with the same id return the cached response)</li>
          </ul>
          <p className="mt-2">Body schema:</p>
          <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto">{`{
  "event": "CLOCK_IN" | "CLOCK_OUT",
  "employee_code": "WLG001",
  "at": "2026-06-08T07:15:00Z",   // optional
  "terminal_id": "till-1",         // optional
  "note": "free-form"              // optional
}`}</pre>
        </div>
      </details>
    </div>
  );
}

function TerminalsCard({ dev }) {
  const [terminals, setTerminals] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ terminal_id: "", site: "", label: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get(`/admin/developers/${dev.id}/terminals`),
        api.get(`/admin/sites`),
      ]);
      setTerminals(t.data.terminals || []);
      setSites((s.data.sites || []).filter((x) => x.is_active));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load terminals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, s] = await Promise.all([
          api.get(`/admin/developers/${dev.id}/terminals`),
          api.get(`/admin/sites`),
        ]);
        if (cancelled) return;
        setTerminals(t.data.terminals || []);
        setSites((s.data.sites || []).filter((x) => x.is_active));
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Failed to load terminals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dev.id]);

  // Use the first active site as the default in the form picker; derived,
  // not stored, so we don't fight the linter's set-state-in-effect rule.
  const defaultSite = sites[0]?.code || "";
  const selectedSite = form.site || defaultSite;

  const submit = async () => {
    const tid = form.terminal_id.trim();
    if (!tid || !selectedSite) {
      toast.error("Terminal ID and site are both required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/developers/${dev.id}/terminals`, {
        terminal_id: tid,
        site: selectedSite,
        label: form.label.trim() || null,
      });
      toast.success(`Registered ${tid} → ${selectedSite}`);
      setAddOpen(false);
      setForm({ terminal_id: "", site: selectedSite, label: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to register terminal");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (terminalId) => {
    if (!window.confirm(`Disable terminal "${terminalId}"? Future requests with this terminal_id will fall back to the employee's primary site.`)) return;
    try {
      await api.post(`/admin/developers/${dev.id}/terminals/${encodeURIComponent(terminalId)}/delete`);
      toast.success(`Disabled ${terminalId}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disable failed");
    }
  };

  const labelFor = (code) => sites.find((s) => s.code === code)?.label || code;
  const active = terminals.filter((t) => t.is_active);
  const inactive = terminals.filter((t) => !t.is_active);
  const bySite = active.reduce((acc, t) => {
    (acc[t.site] = acc[t.site] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="rounded-lg border border-blue-200/60 bg-white p-4 space-y-3" data-testid="terminals-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-blue-100"><MapPin className="h-4 w-4 text-[#1A2744]" /></div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Multisite terminal registry</div>
            <h4 className="font-heading text-base font-bold text-[#1A2744]">POS terminals bound to sites</h4>
          </div>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#1A2744] hover:bg-[#0F1A30]" data-testid="add-terminal-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Register terminal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Register a POS terminal</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600">Terminal ID *</label>
                <Input
                  placeholder="till-ch-1"
                  value={form.terminal_id}
                  onChange={(e) => setForm({ ...form, terminal_id: e.target.value })}
                  data-testid="terminal-id-input"
                />
                <div className="text-[11px] text-gray-500 mt-1">The exact value the POS will send as <code>terminal_id</code>.</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Site *</label>
                <Select value={selectedSite} onValueChange={(v) => setForm({ ...form, site: v })}>
                  <SelectTrigger data-testid="terminal-site-select"><SelectValue placeholder="Select a site" /></SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.label} ({s.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Label (optional)</label>
                <Input
                  placeholder="Front counter / Drive-through"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  data-testid="terminal-label-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="terminal-submit-btn">
                {busy ? "Registering…" : "Register"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading terminals…</div>}

      {!loading && active.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          No terminals registered yet. Inbound clock events will fall back to each employee&apos;s primary site.
        </div>
      )}

      {!loading && active.length > 0 && (
        <div className="space-y-3">
          {Object.entries(bySite).map(([siteCode, rows]) => (
            <div key={siteCode}>
              <div className="text-[11px] font-semibold tracking-widest text-gray-500 uppercase mb-1">
                {labelFor(siteCode)} <span className="font-mono text-gray-400">({siteCode})</span> · {rows.length}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Terminal ID</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-[100px] text-right">Calls</TableHead>
                    <TableHead className="w-[180px]">Last seen</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t.id} data-testid={`terminal-row-${t.terminal_id}`}>
                      <TableCell className="font-mono text-xs">{t.terminal_id}</TableCell>
                      <TableCell className="text-xs">{t.label || <span className="text-gray-400">—</span>}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{t.request_count || 0}</TableCell>
                      <TableCell className="text-xs text-gray-500">{t.last_seen_at ? fmt(t.last_seen_at) : "never"}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => disable(t.terminal_id)} data-testid={`disable-terminal-${t.terminal_id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      {!loading && inactive.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500">Disabled terminals ({inactive.length})</summary>
          <ul className="mt-2 space-y-1">
            {inactive.map((t) => (
              <li key={t.id} className="font-mono text-gray-500">
                {t.terminal_id} — {labelFor(t.site)} · {t.request_count || 0} calls
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="text-[11px] text-gray-500 leading-relaxed pt-2 border-t border-gray-100">
        When the POS posts <code>{"{ terminal_id: \"till-ch-1\", ... }"}</code> we stamp the resulting shift with the
        registered site instead of the employee&apos;s home site — so staff who roam across cafés get clocked at the
        site they&apos;re actually working at.
      </div>
    </div>
  );
}

function DeveloperTable({ rows, emptyHint, renderActions, onSelect }) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Partner</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">{emptyHint || "No rows."}</TableCell></TableRow>
            )}
            {rows.map((d) => (
              <TableRow key={d.id} data-testid={`dev-row-${d.id}`} className="cursor-pointer" onClick={() => onSelect && onSelect(d)}>
                <TableCell>
                  <div className="font-semibold text-[#1A2744]">{d.name}</div>
                  <div className="text-xs text-gray-500">{d.company || "—"}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <a href={`mailto:${d.email}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                    <Mail className="inline h-3 w-3 mr-1" />{d.email}
                  </a>
                </TableCell>
                <TableCell><StatusBadge status={d.status} /></TableCell>
                <TableCell className="text-xs text-gray-500">{fmt(d.last_used_at)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {renderActions(d)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
